import { ReactNode, useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { useToast, UseToastOptions } from "@chakra-ui/react";
import UserError from "lib/errors/UserError";
import { getApolloErrorPresentation } from "lib/apollo/getApolloErrorMessages";
import getServerErrorMessage from "lib/apollo/getServerErrorMessage";
import useLatestRef from "utils/react/useLatestRef";
import getMaybeList from "components/UI/Text/getMaybeList";
import ToastError from "./ToastError";

const DEFAULT_OPTIONS: UseToastOptions = { isClosable: true, duration: 4000 };

type Options = {
  networkStatusMessages?: { [status: string]: string };
};

export default function useAsyncCompleteToast({
  networkStatusMessages,
}: Options = {}) {
  const latestNetworkStatusMessagesRef = useLatestRef(networkStatusMessages);

  const showToast = useToast(DEFAULT_OPTIONS);

  const showSuccessToast = useCallback(
    (successMessage: ReactNode = "Success!", options?: UseToastOptions) =>
      showToast({
        status: "success",
        duration: 2500,
        title: successMessage,
        ...options,
      }),
    [showToast],
  );

  const showErrorToast = useCallback(
    (error?: unknown, options?: UseToastOptions) => {
      const defaultErrorToastOptions: UseToastOptions = { status: "error" };

      if (error && error instanceof ToastError) {
        return showToast({
          ...defaultErrorToastOptions,
          ...error.toastOptions,
          ...options,
        });
      }

      if (error && error instanceof UserError) {
        return showToast({
          ...defaultErrorToastOptions,
          status: error.status,
          title: error.title,
          description: error.description,
          ...options,
        });
      }

      if (error && error instanceof ApolloError) {
        const serverErrorMessage = getServerErrorMessage(
          error,
          latestNetworkStatusMessagesRef.current,
        );
        return showToast({
          ...defaultErrorToastOptions,
          title: "An error occurred making the request",
          description:
            serverErrorMessage ||
            getMaybeList(getApolloErrorPresentation(error)),
          ...options,
        });
      }

      return showToast({
        ...defaultErrorToastOptions,
        title: "An unexpected error occurred",
        ...options,
      });
    },
    [showToast, latestNetworkStatusMessagesRef],
  );

  return { showToast, showSuccessToast, showErrorToast };
}
